/**
 * FeedbackDetailPage — admin moderation view. Lives at /feedback/:id.
 *
 * Full feedback body, attachments, activity timeline, plus action panel:
 *   • Change status (workflow rules enforced server-side)
 *   • Change priority
 *   • Reply (optionally transition to a status in the same request)
 *   • Soft-delete
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { cn } from '../../../infrastructure/lib/utils';
import {
  deleteFeedback,
  getFeedback,
  respondToFeedback,
  updateFeedbackPriority,
  updateFeedbackStatus,
  type AdminFeedback,
  type AdminFeedbackPriority,
  type AdminFeedbackStatus,
} from '../../../infrastructure/lib/api';
import {
  FEEDBACK_PRIORITIES,
  FEEDBACK_STATUSES,
  PRIORITY_TONE,
  STATUS_TONE,
} from './constants';

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export default function FeedbackDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<AdminFeedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState('');
  const [replyStatus, setReplyStatus] = useState<AdminFeedbackStatus | ''>('RESOLVED');

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getFeedback(id)
      .then(setFeedback)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function handle<T>(fn: () => Promise<T>) {
    setBusy(true);
    setError(null);
    try {
      const result = await fn();
      if (result && typeof result === 'object' && 'id' in (result as object)) {
        setFeedback(result as unknown as AdminFeedback);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  async function onStatusChange(next: AdminFeedbackStatus) {
    if (!id || !feedback || feedback.status === next) return;
    await handle(() => updateFeedbackStatus(id, next));
  }

  async function onPriorityChange(next: AdminFeedbackPriority) {
    if (!id || !feedback || feedback.priority === next) return;
    await handle(() => updateFeedbackPriority(id, next));
  }

  async function onReply() {
    if (!id || !reply.trim()) return;
    await handle(() => respondToFeedback(id, reply.trim(), replyStatus || undefined));
    setReply('');
  }

  async function onDelete() {
    if (!id || !window.confirm('Soft-delete this feedback? Audit log is preserved.')) return;
    setBusy(true);
    try {
      await deleteFeedback(id);
      navigate('/feedback', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-40 rounded-2xl bg-surface-container-lowest border border-border-subtle animate-pulse" />
        <div className="h-32 rounded-2xl bg-surface-container-lowest border border-border-subtle animate-pulse" />
      </div>
    );
  }

  if (error && !feedback) {
    return (
      <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
        {error}
      </div>
    );
  }

  if (!feedback) return null;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <Link to="/feedback" className="inline-flex items-center gap-2 text-sm text-on-surface-variant hover:text-on-surface">
          <ArrowLeft className="w-4 h-4" />
          Back to feedback
        </Link>
        <button
          onClick={() => void onDelete()}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 text-sm disabled:opacity-50"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.article
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 space-y-6"
        >
          <section className="bg-surface-container-lowest border border-border-subtle rounded-2xl p-6 space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-on-surface-variant">
              <span className="font-semibold">{feedback.type}</span>
              <span>·</span>
              <span className={PRIORITY_TONE[feedback.priority]}>{feedback.priority}</span>
              <span>·</span>
              <span>{formatDateTime(feedback.createdAt)}</span>
              <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 ml-auto', STATUS_TONE[feedback.status])}>
                {feedback.status.replace('_', ' ')}
              </span>
            </div>
            <h1 className="font-display text-2xl font-bold">{feedback.title}</h1>
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{feedback.description}</p>
            {feedback.rating !== null && (
              <p className="text-xs text-on-surface-variant">User rating: {feedback.rating}/5</p>
            )}
            <div className="text-xs text-on-surface-variant">
              From:{' '}
              <span className="text-on-surface font-semibold">
                {feedback.user?.name ?? '—'}
              </span>{' '}
              ({feedback.user?.email ?? feedback.userId})
            </div>

            {feedback.attachments.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-wide text-on-surface-variant mb-2">
                  Attachments
                </h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {feedback.attachments.map((a) => (
                    <a
                      key={a.id}
                      href={a.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aspect-square rounded-xl border border-border-subtle bg-white/5 overflow-hidden hover:border-electric-violet/40"
                    >
                      {a.fileType.startsWith('image/') ? (
                        <img src={a.fileUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-on-surface-variant">
                          {a.fileType.split('/').pop()?.toUpperCase()}
                        </div>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </section>

          {feedback.adminResponse && (
            <section className="rounded-2xl border border-electric-violet/30 bg-electric-violet/5 p-6">
              <h2 className="text-xs font-bold uppercase tracking-wide text-electric-violet">
                Last reply
              </h2>
              <p className="mt-2 whitespace-pre-wrap text-sm">{feedback.adminResponse}</p>
            </section>
          )}

          <section className="bg-surface-container-lowest border border-border-subtle rounded-2xl p-6">
            <h2 className="text-sm font-bold">Activity</h2>
            <ol className="mt-4 space-y-3 text-sm">
              {(feedback.activity ?? []).map((a) => (
                <li key={a.id} className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-electric-violet shrink-0" />
                  <div className="min-w-0">
                    <div>{describeActivity(a)}</div>
                    <div className="text-xs text-on-surface-variant">{formatDateTime(a.createdAt)}</div>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </motion.article>

        <aside className="space-y-4">
          <div className="bg-surface-container-lowest border border-border-subtle rounded-2xl p-5 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wide text-on-surface-variant">Status</label>
              <select
                value={feedback.status}
                disabled={busy}
                onChange={(e) => void onStatusChange(e.target.value as AdminFeedbackStatus)}
                className="mt-1 w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 text-sm"
              >
                {FEEDBACK_STATUSES.map((s) => (
                  <option key={s} value={s}>{s.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-on-surface-variant">Priority</label>
              <select
                value={feedback.priority}
                disabled={busy}
                onChange={(e) => void onPriorityChange(e.target.value as AdminFeedbackPriority)}
                className="mt-1 w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 text-sm"
              >
                {FEEDBACK_PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="bg-surface-container-lowest border border-border-subtle rounded-2xl p-5 space-y-3">
            <h3 className="text-sm font-bold">Reply to user</h3>
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={5}
              placeholder="Hi! Thanks for reporting this — we've shipped a fix in v1.4.1."
              className="w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 text-sm resize-y"
            />
            <div>
              <label className="text-xs uppercase tracking-wide text-on-surface-variant">
                After replying, set status to
              </label>
              <select
                value={replyStatus}
                onChange={(e) => setReplyStatus(e.target.value as AdminFeedbackStatus | '')}
                className="mt-1 w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 text-sm"
              >
                <option value="">(keep current)</option>
                {FEEDBACK_STATUSES.map((s) => (
                  <option key={s} value={s}>{s.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => void onReply()}
              disabled={busy || !reply.trim()}
              className="w-full px-4 py-2.5 rounded-xl bg-electric-violet text-white text-sm font-semibold hover:bg-electric-violet/90 disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Send reply'}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}

function describeActivity(a: { action: string; metadata: Record<string, unknown> }) {
  switch (a.action) {
    case 'CREATED':
      return 'Submitted by user';
    case 'STATUS_CHANGED':
      return `Status: ${a.metadata.from} → ${a.metadata.to}`;
    case 'PRIORITY_CHANGED':
      return `Priority: ${a.metadata.from} → ${a.metadata.to}`;
    case 'RESPONDED':
      return 'Admin reply sent';
    case 'DELETED':
      return 'Soft-deleted';
    case 'ATTACHMENT_ADDED':
      return 'Attachment added';
    default:
      return a.action;
  }
}
